import { devWarn, devError, devInfo } from "@/lib/logger";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getUserRole, adminUsers, adminActivity, adminMarketplace, adminRevenue,
  adminSetRole, adminTaxExportUrl, adminWaitlistExportUrl, adminWaitlist, taxExportUrl,
  brandingStatus, brandingSyncStripe,
} from "@/lib/api";
import { AdminCharts } from "@/components/admin/AdminCharts";
import { OwnerOpsCard } from "@/components/admin/OwnerOpsCard";
import { InsightsAndSafetyCard } from "@/components/admin/InsightsAndSafetyCard";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

// Static column headers — extracted to module scope so they aren't re-created every render.
const HEADER_CLS = "text-zinc-200 text-[10px] tracking-[0.25em] uppercase";
const USERS_HEADERS = [
  { v: "Email", w: "3fr", cls: HEADER_CLS },
  { v: "Role", w: "1fr", cls: HEADER_CLS },
  { v: "Connect", w: "1fr", cls: HEADER_CLS },
  { v: "Actions", w: "2fr", cls: HEADER_CLS + " text-right" },
];
const MP_HEADERS = [
  { v: "Date", w: "1fr", cls: HEADER_CLS },
  { v: "Category", w: "1.2fr", cls: HEADER_CLS },
  { v: "Gross / Fee / Payout", w: "2fr", cls: HEADER_CLS },
  { v: "Status", w: "0.7fr", cls: HEADER_CLS },
];

const roleColor = (role) => {
  if (role === "owner") return "text-[#22c55e]";
  if (role === "admin") return "text-[#60a5fa]";
  return "text-zinc-200";
};

const Card = ({ title, accent = "#60a5fa", children, testId, right }) => (
  <section data-testid={testId} className="border border-zinc-800 bg-zinc-950/60 p-5">
    <div className="mb-4 flex items-center justify-between">
      <div className="text-[10px] tracking-[0.3em] uppercase font-mono" style={{ color: accent }}>{title}</div>
      {right}
    </div>
    {children}
  </section>
);

const Row = ({ cols, testId, highlight }) => (
  <div
    data-testid={testId}
    className={`grid gap-2 px-3 py-2.5 font-mono text-xs border-b border-zinc-900 ${highlight ? "bg-[#60a5fa]/5" : ""}`}
    style={{ gridTemplateColumns: cols.map((c) => c.w).join(" ") }}
  >
    {cols.map((c, i) => (
      <div key={`col-${i}-${c.w}`} className={`truncate ${c.cls || "text-zinc-200"}`}>{c.v}</div>
    ))}
  </div>
);

const WaitlistPanel = ({ isOwner }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    adminWaitlist().then(setData).catch(() => setData({ count: 0, items: [] }));
  }, []);
  if (!data) return <div className="text-zinc-300 font-mono text-xs">loading…</div>;
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-heading text-4xl font-black neon-green" data-testid="waitlist-total-count">{data.count}</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mt-1">Total signups</div>
        </div>
        {isOwner && (
          <a
            data-testid="admin-waitlist-download"
            href={adminWaitlistExportUrl()}
            className="border border-[#a855f7]/50 hover:bg-[#a855f7]/20 px-4 py-2 text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono font-bold transition-colors"
          >
            Download waitlist CSV →
          </a>
        )}
      </div>
      {data.items.length === 0 ? (
        <div className="text-xs text-zinc-300 font-mono">// no signups yet — share the landing page link</div>
      ) : (
        <div className="border border-zinc-800 max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-950 border-b border-zinc-800">
              <tr className="text-left text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2 text-right">Referrals</th>
                <th className="px-3 py-2">Signed up</th>
              </tr>
            </thead>
            <tbody data-testid="waitlist-rows">
              {data.items.slice(0, 100).map((row) => (
                <tr key={row.email} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                  <td className="px-3 py-2 text-white font-mono truncate">{row.email}</td>
                  <td className="px-3 py-2 text-zinc-200">{row.source || "-"}</td>
                  <td className="px-3 py-2 text-right text-[#a855f7]">{row.referral_count || 0}</td>
                  <td className="px-3 py-2 text-zinc-300 font-mono">{(row.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [mp, setMp] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [busy, setBusy] = useState(null);
  const [branding, setBranding] = useState(null);
  const [brandingBusy, setBrandingBusy] = useState(false);
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear);

  useEffect(() => {
    (async () => {
      try {
        const r = await getUserRole();
        setRole(r);
        if (!r.is_admin) {
          toast.error("Admin access only");
          navigate("/dashboard", { replace: true });
          return;
        }
        const [u, a, m, rev] = await Promise.all([
          adminUsers(), adminActivity(200), adminMarketplace(), adminRevenue(),
        ]);
        setUsers(u.items || []);
        setActivity(a.items || []);
        setMp(m.items || []);
        setRevenue(rev);
        try {
          const b = await brandingStatus();
          setBranding(b);
        } catch (err) {
          devWarn("branding status probe failed:", err?.message || err);
        }
      } catch (err) {
        toast.error("Admin load failed: " + (err?.response?.data?.detail || err.message));
      }
    })();
  }, [navigate]);

  const promote = async (uid, newRole) => {
    setBusy(uid);
    try {
      await adminSetRole(uid, newRole);
      toast.success(`Role set to ${newRole}`);
      const u = await adminUsers();
      setUsers(u.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Set role failed");
    } finally {
      setBusy(null);
    }
  };

  const syncBranding = async () => {
    setBrandingBusy(true);
    try {
      const r = await brandingSyncStripe();
      if (r.auto_applied) {
        toast.success("Branding synced to Stripe — live on next invoice/receipt");
      } else if (r.dashboard_url) {
        toast.success("Logo uploaded to Stripe · finish in Dashboard", {
          duration: 20000,
          description: r.instructions,
          action: { label: "Open Stripe", onClick: () => window.open(r.dashboard_url, "_blank") },
        });
      } else {
        toast.success("Branding synced");
      }
      const b = await brandingStatus();
      setBranding(b);
      devInfo("[branding] Stripe sync response:", r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setBrandingBusy(false);
    }
  };

  if (!role) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 font-mono flex items-center justify-center text-xs tracking-[0.3em] uppercase">
        Checking access…
      </div>
    );
  }
  if (!role.is_admin) return null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-page">
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <Link to="/dashboard" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white" data-testid="admin-back">
              ← Dashboard
            </Link>
            <h1 className="font-heading text-3xl md:text-4xl font-black mt-1">Admin Panel</h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase font-mono text-[#22c55e]">
              {role.role} · {role.is_owner ? "unrestricted" : "read-only"}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Revenue */}
        <Card title="// revenue · lifetime" accent="#22c55e" testId="admin-revenue-card">
          {revenue ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Metric label="Marketplace jobs" value={revenue.marketplace.job_count} sub={fmt(revenue.marketplace.gross_cents) + " gross"} accent="#22c55e" testId="rev-mp"/>
              <Metric label="Platform fees (all)" value={fmt(revenue.total_platform_fees_cents)} sub="20% split" accent="#60a5fa" testId="rev-fees"/>
              <Metric label="Worker payouts" value={fmt(revenue.marketplace.worker_payouts_cents)} sub="80% split" accent="#e2e8f0" testId="rev-payouts"/>
              <Metric label="Invoices (NC)" value={revenue.invoices.count} sub={fmt(revenue.invoices.gross_cents)} accent="#a855f7" testId="rev-inv"/>
            </div>
          ) : <div className="text-zinc-300 font-mono text-xs">loading…</div>}
        </Card>

        {/* Data Dashboard graphs */}
        <AdminCharts />

        {/* Waitlist (test launch) */}
        <Card
          testId="admin-waitlist-card"
          title="// waitlist · payments beta signups"
          subtitle={role.is_owner ? "Live count + one-click CSV export" : "Live count (owner can export CSV)"}
          accent="#a855f7"
        >
          <WaitlistPanel isOwner={!!role.is_owner} />
        </Card>

        {/* Owner Ops — push test, digest preview, unlock message editor (owner only) */}
        {role.is_owner && <OwnerOpsCard />}

        {/* Waitlist Insights + Anti-Fraud + Multi-Owner Approvals */}
        <InsightsAndSafetyCard isOwner={!!role.is_owner} />

        {/* Branding sync — appears on every client surface (invoices, receipts, Checkout) */}
        <Card title="// brand · stripe branding" accent="#e2e8f0" testId="admin-branding-card"
          right={
            <button
              data-testid="admin-branding-sync"
              onClick={syncBranding}
              disabled={brandingBusy || !branding?.stripe?.configured}
              className="text-[10px] tracking-[0.3em] uppercase text-black bg-white hover:bg-zinc-200 px-3 py-2 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {brandingBusy ? "Syncing…" : branding?.stripe?.has_logo ? "Re-sync logo" : "Sync logo to Stripe"}
            </button>
          }
        >
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="border border-zinc-800 bg-black p-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono mb-2">NC Construction LLC</div>
              <div className="bg-zinc-950 border border-zinc-900 h-16 flex items-center justify-center p-2">
                {branding?.logo_nc ? (
                  <img src={branding.logo_nc} alt="NC Construction" className="max-h-full max-w-full object-contain" />
                ) : <span className="text-zinc-400 text-xs font-mono">no logo url</span>}
              </div>
            </div>
            <div className="border border-zinc-800 bg-black p-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 font-mono mb-2">Clark Properties</div>
              <div className="bg-zinc-950 border border-zinc-900 h-16 flex items-center justify-center p-2">
                {branding?.logo_clark ? (
                  <img src={branding.logo_clark} alt="Clark Properties" className="max-h-full max-w-full object-contain" />
                ) : <span className="text-zinc-400 text-xs font-mono">no logo url</span>}
              </div>
            </div>
          </div>
          <div className="border border-zinc-900 bg-black p-3 text-xs font-mono">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Stripe logo</div>
                <div className={`mt-1 ${branding?.stripe?.has_logo ? "text-[#22c55e]" : "text-zinc-200"}`}>
                  {branding?.stripe?.has_logo ? "✓ set" : "not synced"}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Stripe icon</div>
                <div className={`mt-1 ${branding?.stripe?.has_icon ? "text-[#22c55e]" : "text-zinc-200"}`}>
                  {branding?.stripe?.has_icon ? "✓ set" : "not synced"}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Primary color</div>
                <div className="mt-1 flex items-center gap-2 text-white">
                  {branding?.stripe?.primary_color && (
                    <span className="inline-block w-3 h-3 border border-zinc-800" style={{ background: branding.stripe.primary_color }} />
                  )}
                  {branding?.stripe?.primary_color || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Emails</div>
                <div className="mt-1 text-[#22c55e]">✓ logos live</div>
              </div>
            </div>
          </div>
          <div className="mt-3 text-[10px] tracking-[0.15em] uppercase text-zinc-300 font-mono leading-relaxed">
            // Applies to Stripe invoices, hosted pay page, receipt emails, and Checkout. Money/Hub emails (verify · digest · alerts) already have the co-branded header live.
          </div>
        </Card>

        {/* Tax exports */}
        <Card title="// tax exports · csv" accent="#a855f7" testId="admin-tax-card"
          right={
            <div className="flex items-center gap-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 font-mono">year</label>
              <select
                data-testid="admin-tax-year"
                value={taxYear}
                onChange={(e) => setTaxYear(Number(e.target.value))}
                className="bg-black border border-zinc-800 text-white text-xs font-mono px-2 py-1"
              >
                {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          }>
          <div className="grid md:grid-cols-2 gap-4">
            {role.is_owner && (
              <a
                data-testid="admin-tax-download-all"
                href={adminTaxExportUrl(taxYear)}
                className="border border-[#a855f7]/40 hover:border-[#a855f7] hover:bg-[#a855f7]/10 p-4 transition-colors block"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono">// platform-wide · owner only</div>
                <div className="text-white text-sm font-bold mt-1">Download {taxYear} marketplace CSV</div>
                <div className="text-xs text-zinc-200 mt-1">Every marketplace transaction · payer + payee · 1099-ready.</div>
              </a>
            )}
            <a
              data-testid="admin-tax-download-me"
              href={taxExportUrl(taxYear)}
              className="border border-[#60a5fa]/40 hover:border-[#60a5fa] hover:bg-[#60a5fa]/10 p-4 transition-colors block"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono">// my income</div>
              <div className="text-white text-sm font-bold mt-1">Download {taxYear} personal CSV</div>
              <div className="text-xs text-zinc-200 mt-1">Your income · fees · payouts. Turbotax H&B compatible.</div>
            </a>
          </div>
        </Card>

        {/* Users */}
        <Card title={`// users · ${users.length}`} accent="#60a5fa" testId="admin-users-card">
          <Row cols={USERS_HEADERS}/>
          {users.map((u, i) => (
            <Row key={`${u.user_id}-${i}`} testId={`admin-user-${u.user_id}-${i}`}
              cols={[
                {v: u.email, w: "3fr", cls: "text-white"},
                {v: u.role, w: "1fr", cls: roleColor(u.role)},
                {v: u.stripe_connect_id ? "✓" : "—", w: "1fr", cls: u.stripe_connect_id ? "text-[#22c55e]" : "text-zinc-400"},
                {v: role.is_owner && u.role !== "owner" ? (
                  <div className="flex gap-2 justify-end">
                    {u.role !== "admin" && (
                      <button
                        data-testid={`admin-promote-${u.user_id}`}
                        disabled={busy === u.user_id}
                        onClick={() => promote(u.user_id, "admin")}
                        className="text-[10px] tracking-[0.25em] uppercase text-[#60a5fa] border border-[#60a5fa]/50 hover:bg-[#60a5fa]/10 px-2 py-1"
                      >Promote →</button>
                    )}
                    {u.role === "admin" && (
                      <button
                        data-testid={`admin-demote-${u.user_id}`}
                        disabled={busy === u.user_id}
                        onClick={() => promote(u.user_id, "user")}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 border border-zinc-800 hover:border-zinc-600 px-2 py-1"
                      >Demote</button>
                    )}
                  </div>
                ) : <span className="text-zinc-400 text-right block text-[10px]">—</span>, w: "2fr"},
              ]}
            />
          ))}
        </Card>

        {/* Marketplace transactions */}
        <Card title={`// marketplace · ${mp.length} jobs`} accent="#22c55e" testId="admin-mp-card">
          {mp.length === 0 ? (
            <div className="text-zinc-300 font-mono text-xs py-4">no marketplace jobs yet.</div>
          ) : (
            <>
              <Row cols={MP_HEADERS}/>
              {mp.slice(0, 50).map((j) => (
                <Row key={j.id} testId={`admin-job-${j.id}`}
                  cols={[
                    {v: (j.paid_at || j.created_at || "").slice(0, 10), w: "1fr", cls: "text-white"},
                    {v: j.service_category, w: "1.2fr", cls: "text-zinc-300"},
                    {v: `${fmt(j.amount_cents)} · ${fmt(j.platform_fee_cents)} · ${fmt(j.worker_amount_cents)}`, w: "2fr", cls: "text-zinc-200"},
                    {v: j.status, w: "0.7fr", cls: j.status === "paid" ? "text-[#22c55e]" : "text-zinc-200"},
                  ]}
                />
              ))}
            </>
          )}
        </Card>

        {/* Activity log */}
        <Card title={`// activity log · ${activity.length}`} accent="#fb7185" testId="admin-activity-card">
          {activity.length === 0 ? (
            <div className="text-zinc-300 font-mono text-xs py-4">no activity yet.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {activity.slice(0, 200).map((a) => (
                <Row key={a.id} testId={`admin-activity-${a.id}`}
                  cols={[
                    {v: (a.created_at || "").slice(0, 19).replace("T", " "), w: "1.5fr", cls: "text-zinc-200"},
                    {v: a.action, w: "1.5fr", cls: "text-white"},
                    {v: a.user_id, w: "1.5fr", cls: "text-zinc-200"},
                    {v: JSON.stringify(a.meta || {}), w: "3fr", cls: "text-zinc-200 truncate"},
                  ]}
                />
              ))}
            </div>
          )}
        </Card>
      </main>

      <footer className="border-t border-zinc-800 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-6 text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">
          Admin panel · owner only · every action is logged.
        </div>
      </footer>
    </div>
  );
}

const Metric = ({ label, value, sub, accent, testId }) => (
  <div data-testid={testId} className="border border-zinc-800 bg-black p-4">
    <div className="text-[10px] tracking-[0.3em] uppercase font-mono" style={{ color: accent }}>{label}</div>
    <div className="text-white text-2xl font-black mt-2">{value}</div>
    {sub && <div className="text-xs text-zinc-200 mt-1 font-mono">{sub}</div>}
  </div>
);
